import { logCoverPrint } from "@/lib/coverPrintLog";
import { documentPaperKey, storedPagePaperSizesJson } from "@/lib/documentPaperOverrides";
import {
  assertPrintJobSucceeded,
  PrintJobFeedbackError,
  queuePrintPackByLocation,
  showPrintJobFeedback,
  waitForPrintJob,
  type PrintPackFile,
  type PrintSides,
} from "@/lib/printJob";
import { setDefaultPrintLocation } from "@/lib/printPreferences";
import { fetchStuecklistePdf } from "@/lib/stuecklisten";
import { toast } from "@/lib/toast";
import { fetchAufmassSheetPdf } from "@/lib/aufmass";
import {
  fetchAufmassDeckblattPdf,
  fetchMontageAbOhnePreisePdf,
  fetchMontageDeckblattPdf,
  fetchMontageunterlageFileBlob,
  fetchWerkstattDeckblattPdf,
  type Montageunterlage,
} from "@/lib/projectOverview";
import type { DocumentPackKind, MontagePackTab } from "@/projectOverview/montageunterlagenTabs";

function isImageContent(blob: Blob, filename?: string | null, contentType?: string | null): boolean {
  if (String(contentType || blob.type || "").toLowerCase().startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(String(filename || ""));
}

function isPdfContent(blob: Blob, filename?: string | null, contentType?: string | null): boolean {
  if (String(contentType || blob.type || "").toLowerCase().includes("pdf")) return true;
  return String(filename || "").toLowerCase().endsWith(".pdf");
}

export function isMissingAuftragsbestaetigungError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err || "");
  const lower = message.toLowerCase();
  return (
    lower.includes("keine auftragsbestätigung") ||
    lower.includes("nicht gefunden") ||
    lower.includes("404")
  );
}

async function loadTabBlob(
  orderId: number,
  tab: MontagePackTab,
  pack: DocumentPackKind = "montage",
): Promise<{ blob: Blob; filename: string; kind: "pdf" | "image" } | null> {
  if (tab.kind === "deckblatt") {
    const blob =
      pack === "werkstatt"
        ? await fetchWerkstattDeckblattPdf(orderId)
        : pack === "aufmass"
          ? await fetchAufmassDeckblattPdf(orderId)
          : await fetchMontageDeckblattPdf(orderId);
    const filename =
      pack === "werkstatt"
        ? "Werkstatt-Deckblatt.pdf"
        : pack === "aufmass"
          ? "Aufmass-Deckblatt.pdf"
          : "Deckblatt.pdf";
    return { blob, filename, kind: "pdf" };
  }
  if (tab.kind === "aufmassblatt") {
    return {
      blob: await fetchAufmassSheetPdf(orderId, tab.sheetId),
      filename: "Brandschutztuer-Aufmass.pdf",
      kind: "pdf",
    };
  }
  if (tab.kind === "ab") {
    try {
      return {
        blob: await fetchMontageAbOhnePreisePdf(orderId),
        filename: "Auftragsbestaetigung.pdf",
        kind: "pdf",
      };
    } catch (err) {
      if (isMissingAuftragsbestaetigungError(err)) return null;
      throw err;
    }
  }
  if (tab.kind === "stueckliste") {
    return {
      blob: await fetchStuecklistePdf(tab.stuecklisteId),
      filename: `${tab.label || "Stueckliste"}.pdf`,
      kind: "pdf",
    };
  }
  if (tab.kind === "file") {
    const item: Montageunterlage = tab.item;
    const blob = await fetchMontageunterlageFileBlob(orderId, item);
    const filename = item.filename || item.title || `Datei-${item.id}`;
    if (isPdfContent(blob, filename, item.content_type)) {
      return {
        blob,
        filename: filename.toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`,
        kind: "pdf",
      };
    }
    if (isImageContent(blob, filename, item.content_type)) {
      return { blob, filename, kind: "image" };
    }
    return null;
  }
  return null;
}

export function formatMontagePackPrintMessage(
  printed: number,
  skipped: number,
  packJobs: number,
): string {
  const docs = printed === 1 ? "1 Dokument" : `${printed} Dokumente`;
  const sent =
    packJobs <= 1
      ? `${docs} als ein Druckauftrag gesendet.`
      : `${docs} in ${packJobs} Druckaufträgen direkt hintereinander gesendet (getrennt nach Papierformat).`;
  if (skipped <= 0) return sent;
  const skipText = skipped === 1 ? "1 Dokument übersprungen." : `${skipped} Dokumente übersprungen.`;
  return `${sent} ${skipText}`;
}

function packTitle(pack: DocumentPackKind): string {
  if (pack === "werkstatt") return "Werkstattunterlagen";
  if (pack === "aufmass") return "Aufmaßunterlagen";
  return "Montageunterlagen";
}

function pageSizesForTab(orderId: number, pack: DocumentPackKind, tabKey: string): string[] | null {
  const raw = storedPagePaperSizesJson(documentPaperKey(orderId, pack, tabKey));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((size): size is string => typeof size === "string");
  } catch {
    return null;
  }
}

function sidesForTab(tab: MontagePackTab, pack: DocumentPackKind): PrintSides {
  if (tab.kind === "deckblatt" && pack === "montage") return "duplex-if-multipage";
  return "one-sided";
}

/**
 * Druckt die gewählten Unterlagen in Anzeigereihenfolge als einen Druckauftrag.
 * Fehler werden als Toast angezeigt (nicht nur geworfen).
 */
export async function printMontagePackByLocation(
  orderId: number,
  tabs: MontagePackTab[],
  locationId: number,
  copies: number,
  pack: DocumentPackKind = "montage",
): Promise<void> {
  if (!tabs.length) {
    toast.show({
      variant: "error",
      title: "Druck fehlgeschlagen",
      message: "Keine Dokumente zum Drucken vorhanden.",
    });
    return;
  }

  await setDefaultPrintLocation(locationId);

  const loadingId = toast.loading("Unterlagen werden zusammengestellt …");
  let skipped = 0;

  try {
    const loaded = await Promise.all(
      tabs.map(async (tab) => {
        try {
          return { tab, item: await loadTabBlob(orderId, tab, pack), error: null as unknown };
        } catch (err) {
          return { tab, item: null, error: err };
        }
      }),
    );

    const failed = loaded.find((entry) => entry.error);
    if (failed) {
      const err = failed.error;
      toast.dismiss(loadingId);
      toast.show({
        variant: "error",
        title: "Druck fehlgeschlagen",
        message:
          err instanceof Error
            ? err.message
            : `Dokument „${failed.tab.label}“ konnte nicht geladen werden.`,
      });
      return;
    }

    const files: PrintPackFile[] = [];
    let includesCover = false;
    for (const entry of loaded) {
      if (!entry.item) {
        skipped += 1;
        continue;
      }
      if (entry.tab.kind === "deckblatt") includesCover = true;
      files.push({
        blob: entry.item.blob,
        filename: entry.item.filename,
        kind: entry.item.kind,
        pagePaperSizes: pageSizesForTab(orderId, pack, entry.tab.key),
        sides: sidesForTab(entry.tab, pack),
      });
    }

    if (!files.length) {
      toast.dismiss(loadingId);
      toast.show({
        variant: "error",
        title: "Druck fehlgeschlagen",
        message:
          skipped > 0
            ? "Keine druckbaren PDFs/Bilder in der Auswahl gefunden."
            : "Keine Dokumente zum Drucken vorhanden.",
      });
      return;
    }

    toast.show({
      id: loadingId,
      variant: "loading",
      message:
        files.length === 1
          ? "Druckauftrag wird gesendet …"
          : `Druckauftrag aus ${files.length} Dokumenten wird gesendet …`,
      duration: 0,
      dismissible: false,
    });

    const queued = await queuePrintPackByLocation(locationId, copies, files, packTitle(pack));
    if (!queued.job_id) {
      throw new Error("Druckauftrag konnte nicht gestartet werden.");
    }
    const job = await waitForPrintJob(queued.job_id, 180_000);
    assertPrintJobSucceeded(job);

    if (includesCover) {
      await logCoverPrint(
        orderId,
        {
          cover_kind: pack === "werkstatt" ? "werkstatt" : pack === "aufmass" ? "aufmass" : "montage",
          location_id: locationId,
          copies,
        },
        job,
      );
    }

    toast.dismiss(loadingId);
    toast.show({
      variant: "success",
      title: "Druck abgeschlossen",
      message: formatMontagePackPrintMessage(
        files.length,
        skipped,
        queued.pack_jobs ?? job.result?.pack_jobs ?? 1,
      ),
    });
  } catch (err) {
    if (err instanceof PrintJobFeedbackError) {
      showPrintJobFeedback(err.job, loadingId);
      return;
    }
    toast.dismiss(loadingId);
    toast.show({
      variant: "error",
      title: "Druck fehlgeschlagen",
      message: err instanceof Error ? err.message : "Unbekannter Fehler beim Drucken.",
    });
  }
}

/** Startet den Druck im Hintergrund – UI kann sofort weiterarbeiten. */
export function startMontagePackPrint(
  orderId: number,
  tabs: MontagePackTab[],
  locationId: number,
  copies: number,
  pack: DocumentPackKind = "montage",
): void {
  void printMontagePackByLocation(orderId, tabs, locationId, copies, pack);
}
